import { PolfEventPokerStatusEnum } from '@polf/shared/codegen/types'
import { formatChips, formatChipsShort } from '@polf/shared/locale'
import { useEffect, useState } from 'react'

import type { PokerLevelFragment } from '../../../__generated__/hooks'
import NextBreakBadge from './NextBreakBadge'

const formatMmSs = (totalSeconds: number): string => {
  const safe = Math.max(0, Math.floor(totalSeconds))
  const m = Math.floor(safe / 60)
  const s = safe % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

const computeRemaining = ({
  startedAt,
  pausedAt,
  durationMinutes,
  now,
}: {
  startedAt: Date | null
  pausedAt: Date | null
  durationMinutes: number
  now: number
}): number => {
  if (!startedAt) return durationMinutes * 60
  const effectiveNow = pausedAt ? pausedAt.getTime() : now
  const elapsedSec = (effectiveNow - startedAt.getTime()) / 1000
  return Math.max(0, durationMinutes * 60 - elapsedSec)
}

/**
 * The poker clock — dark hero card with massive countdown, SB/BB/Ante,
 * next-level peek, a NextBreakBadge when a break is one hop away, plus
 * the tournament-wide field stats (players left + avg stack).
 *
 * Card owns ALL global tournament state. Personal stats (my stack, my
 * BBs, my pay-jump) live on `SeatCard`. See design.md §6.9 — clock card
 * is the "projector in your pocket": what's true for the room, not me.
 *
 * Self-owned 500ms tick keeps the countdown smooth between 3s polls.
 *
 * Three states drive number colour:
 *  - urgent (≤ 10s, not paused/break): pulsing flag-red
 *  - low time (≤ 60s, not paused/break): warn yellow
 *  - normal: paper white
 */
const Clock = ({
  level,
  nextLevel,
  inBreak,
  startedAt,
  pausedAt,
  levelIndex,
  status,
  playersLeft,
  playersTotal,
  averageStack,
  onExpand,
}: {
  level: PokerLevelFragment
  nextLevel: PokerLevelFragment | null
  inBreak: boolean
  startedAt: Date | null
  pausedAt: Date | null
  levelIndex: number
  status: PolfEventPokerStatusEnum
  playersLeft: number | null | undefined
  playersTotal: number | null | undefined
  averageStack: number | null | undefined
  /** Optional — when provided, an expand glyph in the top-right opens
   *  the fullscreen "projector in your pocket" overlay. Omit on
   *  spectator surfaces (EliminatedView). */
  onExpand?: () => void
}) => {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 500)
    return () => window.clearInterval(id)
  }, [])

  // In-break: countdown runs against the level's breakAfterMinutes,
  // not its own durationMinutes.
  const activeDuration = inBreak
    ? level.breakAfterMinutes ?? 0
    : level.durationMinutes
  const remaining = computeRemaining({
    startedAt,
    pausedAt,
    durationMinutes: activeDuration,
    now,
  })

  const isPaused = status === PolfEventPokerStatusEnum.Paused
  const lowTime = remaining <= 60 && !isPaused && !inBreak
  const urgent = remaining <= 10 && !isPaused && !inBreak
  let clockColor = 'text-paper'
  if (lowTime) clockColor = urgent ? 'text-flag' : 'text-warn'

  // Show "Pause om X min" badge when the current level has a break
  // attached (and we're not already in it). Ticks locally.
  const showBreakBadge = !inBreak && (level.breakAfterMinutes ?? 0) > 0
  const minsToBreak = showBreakBadge
    ? Math.max(0, Math.ceil(remaining / 60))
    : null

  return (
    <div
      className="relative overflow-hidden mx-2 my-3 text-paper rounded-lg p-4 shadow flex flex-col gap-3"
      style={{
        // Dark-night green per design.md §6.9 — distinct from the
        // golf hero (which uses the daytime green-800 gradient).
        background: 'linear-gradient(180deg, #0e2418 0%, #050d09 100%)',
      }}
    >
      {/* Red radial glow top-right — keeps the deep-black surface
          from going flat and signals "this is the live arena". */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 90% 0%, rgba(230, 65, 24, 0.18), transparent 55%)',
        }}
      />
      <div className="relative flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] up text-green-100/80">Nivå</div>
          <div className="font-display font-bold text-base mt-0.5">
            #{levelIndex + 1}
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="text-right">
            <div className="text-[10px] up text-green-100/80">Blinds</div>
            <div className="font-display font-bold text-base mt-0.5 num">
              {inBreak
                ? 'PAUSE'
                : `${formatChips(level.smallBlind)} / ${formatChips(
                    level.bigBlind
                  )}`}
            </div>
          </div>
          {/* Fullscreen toggle — discrete on purpose. Premium poker apps
                put a quiet expand glyph here so the player can flatten
                the phone on the rail and turn the screen into a clock
                board. Hidden when no handler is wired (spectator
                surfaces like EliminatedView). */}
          {onExpand ? (
            <button
              type="button"
              onClick={onExpand}
              aria-label="Vis klokken i fullskjerm"
              className="text-paper/60 active:opacity-60 -mt-0.5 -mr-1 p-1.5"
            >
              <svg
                viewBox="0 0 24 24"
                width={18}
                height={18}
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M3 9V3h6" />
                <path d="M21 9V3h-6" />
                <path d="M3 15v6h6" />
                <path d="M21 15v6h-6" />
              </svg>
            </button>
          ) : null}
        </div>
      </div>
      <div
        className={`relative text-center font-display font-extrabold leading-none num ${clockColor}`}
        style={{
          fontSize: 64,
          letterSpacing: '-0.04em',
          transition: 'color 200ms ease',
          animation: urgent
            ? 'polf-pulse-red 0.9s ease-in-out infinite'
            : undefined,
        }}
      >
        {formatMmSs(remaining)}
      </div>
      {minsToBreak !== null ? (
        <div className="relative flex justify-center">
          <NextBreakBadge minutes={minsToBreak} />
        </div>
      ) : null}
      {/* Field stats — players left · avg stack. These are tournament-wide
          numbers, so they belong on the clock card (the global lens), not
          on SeatCard (the personal lens). Real live-tournament projectors
          show the same trio: level, time, field. */}
      {playersLeft != null || (averageStack != null && averageStack > 0) ? (
        <div className="relative flex items-center justify-center gap-3 text-[11px] text-green-100/80 border-t border-paper/10 pt-2.5">
          {playersLeft != null ? (
            <span>
              <span className="font-mono num font-bold text-paper">
                {playersLeft}
                {playersTotal != null ? `/${playersTotal}` : ''}
              </span>{' '}
              igjen
            </span>
          ) : null}
          {playersLeft != null && averageStack != null && averageStack > 0 ? (
            <span className="text-green-100/40">·</span>
          ) : null}
          {averageStack != null && averageStack > 0 ? (
            <span>
              snitt{' '}
              <span className="font-mono num font-bold text-paper">
                {formatChipsShort(averageStack)}
              </span>
            </span>
          ) : null}
        </div>
      ) : null}
      {!inBreak ? (
        <div className="relative grid grid-cols-3 gap-2 text-center border-t border-paper/10 pt-3">
          <div>
            <div className="text-[10px] up text-green-100/80">SB</div>
            <div className="font-mono num font-bold text-sm">
              {formatChips(level.smallBlind)}
            </div>
          </div>
          <div>
            <div className="text-[10px] up text-green-100/80">BB</div>
            <div className="font-mono num font-bold text-sm">
              {formatChips(level.bigBlind)}
            </div>
          </div>
          <div>
            <div className="text-[10px] up text-green-100/80">Ante</div>
            <div className="font-mono num font-bold text-sm">
              {level.ante > 0 ? formatChips(level.ante) : '—'}
            </div>
          </div>
        </div>
      ) : null}
      {nextLevel ? (
        <div className="relative text-[11px] text-green-100/80 text-center">
          Neste:{' '}
          <span className="font-semibold text-paper">
            {`${formatChips(nextLevel.smallBlind)}/${formatChips(
              nextLevel.bigBlind
            )}`}
          </span>{' '}
          · om {Math.ceil(remaining / 60)} min
        </div>
      ) : null}
    </div>
  )
}

export default Clock
